import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import FlakyIcon from '@mui/icons-material/Flaky';
import { ThemeProvider } from '@mui/material/styles';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExploreIcon from '@mui/icons-material/Explore';
import { FILE_MANAGER_PERMISSIONS } from '@/constants/FileManagerConstants';
import HelpIcon from '@mui/icons-material/Help';
import RuleIcon from '@mui/icons-material/Rule';
import FlagIcon from '@mui/icons-material/Flag';
import BookIcon from '@mui/icons-material/Book';
import FolderIcon from '@mui/icons-material/Folder';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import NavbarMenuButton from './NavbarMenuButton';
import LogoutButton from '././LogoutButton';
import React, { useContext, useState } from 'react';
import { UserContext } from '../../context/userContext';
import PlaylistAddCheckCircleRoundedIcon from '@mui/icons-material/PlaylistAddCheckCircleRounded';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import theme from '@/theme';
import {
  Grid,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography as MuiTypography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useServices } from '@/components/ServiceContext.tsx';
import { CustomRouteObject } from '@/service/RouteService.ts';
import { isUserInGroup } from '@/auth/EricksonAuthenticator.tsx';

interface ButtonDefinition {
  id: string;
  name?: string;
  path?: string;
  icon: React.ReactElement;
}

const addIconsToRoute = (
  buttonDefinitions: ButtonDefinition[],
  routes?: CustomRouteObject[]
) => {
  return buttonDefinitions.map((buttonDefinition: ButtonDefinition) => {
    const associatedRoute = routes?.find(r => r.id === buttonDefinition.id);
    if (!associatedRoute || !associatedRoute.label || !associatedRoute.path) {
      throw new Error(
        `Route for nav button ${buttonDefinition.id} is missing or incomplete.`
      );
    }
    return {
      ...buttonDefinition,
      name: associatedRoute.label,
      path: associatedRoute.path,
    };
  });
};

const Navbar: React.FC = () => {
  const { routeService } = useServices();
  const navbarButtons = addIconsToRoute(
    [
      { id: 'reports', icon: <AssessmentIcon /> },
      { id: 'bought-list-opt-out', icon: <FlakyIcon /> },
      {
        id: 'bought-list',
        icon: <PlaylistAddCheckCircleRoundedIcon color="primary" />,
      },
      { id: 'house-list', icon: <HelpIcon /> },
    ],
    routeService.routes
  );

  // Define admin routes separately
  let adminRoutes: ButtonDefinition[] = [];
  const userContext = useContext(UserContext);

  const isAdmin = isUserInGroup(userContext.user, 'Admin');
  const hasFileManagerAccess = isUserInGroup(
    userContext.user,
    FILE_MANAGER_PERMISSIONS.ALLOWED_GROUPS
  );

  if (isAdmin || hasFileManagerAccess) {
    const baseAdminRoutes: ButtonDefinition[] = [];

    if (isAdmin) {
      baseAdminRoutes.push(
        { id: 'admin', icon: <ManageAccountsIcon /> },
        { id: 'data-transfer', icon: <WorkHistoryIcon /> },
        { id: 'data-explorer', icon: <ExploreIcon /> },
        { id: 'exclusion-rules', icon: <RuleIcon /> },
        { id: 'ml-inactive-flags', icon: <FlagIcon /> },
        { id: 'flag-dictionary', icon: <BookIcon /> }
      );
    }

    if (hasFileManagerAccess) {
      baseAdminRoutes.push({ id: 'file-manager', icon: <FolderIcon /> });
    }

    adminRoutes = addIconsToRoute(baseAdminRoutes, routeService.routes);
  }

  const navigate = useNavigate();
  const [adminAnchorEl, setAdminAnchorEl] = useState<null | HTMLElement>(null);

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAdminAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminAnchorEl(null);
  };

  const handleAdminMenuItemClick = (path?: string) => {
    if (path) {
      navigate(path, { viewTransition: true });
    }
    handleAdminMenuClose();
  };

  return userContext.user ? (
    <ThemeProvider theme={theme}>
      <AppBar position="static" elevation={0}>
        <Container maxWidth={false}>
          <Toolbar
            disableGutters
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              py: { xs: 1, sm: 0 },
            }}
          >
            {/* Logo and Title */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'center', sm: 'flex-start' },
                mb: { xs: 1, sm: 0 },
              }}
            >
              <Link
                component="button"
                onClick={() =>
                  navigate(
                    routeService.routes?.find(r => r.id === 'home')?.path ||
                      '/',
                    { viewTransition: true }
                  )
                }
              >
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <Box
                      component="img"
                      sx={{
                        maxHeight: 70,
                        paddingTop: 2,
                        paddingBottom: 1.25,
                      }}
                      alt="Erickson Senior Living Logo"
                      src={'/images/ESL_CORP_2C_RVSD_Horiz.png'}
                    />
                  </Grid>
                  <Grid item>
                    <Typography
                      variant="h2"
                      sx={{
                        color: '#fff',
                        fontWeight: 500,
                        fontSize: { lg: '2.25rem', md: 0, sm: 0, xs: 0 },
                        ml: 2,
                      }}
                    >
                      List Logic
                    </Typography>
                  </Grid>
                </Grid>
              </Link>
            </Box>

            {/* Navigation Buttons */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                flexGrow: 1,
                gap: 1,
              }}
            >
              {navbarButtons.map(navbarButton => (
                <NavbarMenuButton
                  key={navbarButton.name}
                  icon={navbarButton.icon}
                  name={navbarButton.name!}
                  path={navbarButton.path!}
                />
              ))}

              {/* Admin Drop Down */}
              {adminRoutes.length > 0 && (
                <>
                  <NavbarMenuButton
                    icon={<AdminPanelSettingsIcon />}
                    name="Admin"
                    onClick={handleAdminMenuOpen}
                  />
                  <Menu
                    anchorEl={adminAnchorEl}
                    open={Boolean(adminAnchorEl)}
                    onClose={handleAdminMenuClose}
                  >
                    {adminRoutes.map(route => (
                      <MenuItem
                        key={route.name}
                        onClick={() => handleAdminMenuItemClick(route.path)}
                      >
                        <ListItemIcon>{route.icon}</ListItemIcon>
                        <MuiTypography variant="inherit">
                          {route.name}
                        </MuiTypography>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>

            {/* Divider & User Info / Logout */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderRightWidth: 2,
                mx: 1,
                display: { xs: 'none', sm: 'block' },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mt: { xs: 1, sm: 0 },
              }}
            >
              <Box
                sx={{
                  textAlign: { sm: 'right' },
                  mr: { xs: 0, sm: '1.5rem' },
                  mt: 1.5,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    textTransform: 'none',
                    mb: -0.5,
                  }}
                  gutterBottom
                >
                  {userContext?.user?.email?.replace('@erickson.com', '')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'none',
                    color: '#808080',
                  }}
                  gutterBottom
                >
                  Logged in
                </Typography>
              </Box>
              <LogoutButton />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </ThemeProvider>
  ) : (
    <LinearProgress />
  );
};

export default Navbar;
